export interface BookingQueryParams {
	roomGuid?: string;
	dealGuid?: string;
	packageGuid?: string;
	accommodationTypes?: string;
	promotionCode?: string;
	languageCode?: string;
	startDate?: string;
	endDate?: string;
	occupancy?: string;
}

export class BookingRedirectHelper {
	private static readonly qParamMapping: Record<
		keyof BookingQueryParams,
		string
	> = {
		roomGuid: 'roomGUID',
		dealGuid: 'dealGUID',
		packageGuid: 'packageGUID',
		accommodationTypes: 'accommodationTypes',
		promotionCode: 'promotionCode',
		languageCode: 'languageCode',
		startDate: 'startDate',
		endDate: 'endDate',
		occupancy: 'occupancy',
	};

	/**
	 * Generates the correct booking tool redirect url based on the provided parameters.
	 * @param bookingToolUrl The base URL of the booking tool.
	 * @param hotelSlug The slug of the hotel. Required for correct routing — if absent, * returns bookingToolUrl unchanged (or "" if that's also absent).
	 * @param queryParams Query parameters to append to the booking tool URL.
	 *
	 * @returns The complete redirect URL for the booking tool, or an empty string
	 * when no booking tool URL is configured (callers fall back to an in-domain
	 * route in that case).
	 */
	static getBookingToolUrl(
		bookingToolUrl: string | undefined,
		hotelSlug: string | undefined,
		queryParams: BookingQueryParams,
	): string {
		if (!bookingToolUrl || !hotelSlug) {
			return bookingToolUrl ?? '';
		}

		const url = new URL(bookingToolUrl);

		url.searchParams.set('hotelSlug', hotelSlug);

		for (const [key, value] of this.getRedirectQueryParams(queryParams)) {
			url.searchParams.set(key, value);
		}

		return url.toString();
	}

	static getRedirectQueryParams(
		queryParams: BookingQueryParams,
	): URLSearchParams {
		const params = new URLSearchParams();

		for (const [key, urlParam] of Object.entries(this.qParamMapping)) {
			const value = queryParams[key as keyof BookingQueryParams];

			if (value !== undefined && value !== null) {
				params.set(urlParam, String(value));
			}
		}

		return params;
	}

	/**
	 * Returns the booking query params as a record keyed by the URL parameter
	 * names — suitable for binding to Angular's `[queryParams]`. Only the keys
	 * present in `queryParams` are written, so callers can omit a field
	 * (e.g. `promotionCode`) instead of clearing it from the existing URL
	 * under `queryParamsHandling: 'merge'`.
	 */
	static toQueryParamRecord(
		queryParams: BookingQueryParams,
	): Record<string, string | undefined> {
		const record: Record<string, string | undefined> = {};
		for (const [key, urlParam] of Object.entries(this.qParamMapping)) {
			if (key in queryParams) {
				record[urlParam] = queryParams[key as keyof BookingQueryParams];
			}
		}
		return record;
	}
}
